// Limpeza completa dos dados do CRM neste navegador (botão "Limpar cache" da barra
// superior). Existe para o caso de o cliente ficar preso numa versão antiga ou
// quebrada do frontend depois de um deploy: apaga service workers, Cache Storage,
// localStorage, sessionStorage, IndexedDB e cookies, encerra a sessão no backend
// (o cookie `jrt` é httpOnly e só o servidor apaga) e recarrega no /login.
//
// É o único lugar onde `localStorage.clear()` é intencional: aqui o usuário pediu
// para apagar tudo, inclusive mensagens pendentes e preferências (ver o aviso em
// helpers/sessionStorageKeys.js).
import api from "../services/api";

// Canal para as outras abas do CRM no mesmo navegador recarregarem junto; sem
// isso elas seguiriam com a versão antiga na memória e regravariam o storage.
export const CLEAR_APP_DATA_CHANNEL = "crm-clear-app-data";

const withTimeout = (promise, ms) =>
  Promise.race([
    promise,
    new Promise((resolve) => setTimeout(resolve, ms)),
  ]).catch(() => {});

const unregisterServiceWorkers = async () => {
  if (!("serviceWorker" in navigator)) return;
  const registrations = await navigator.serviceWorker.getRegistrations();
  await Promise.all(registrations.map((reg) => reg.unregister()));
};

const clearCacheStorage = async () => {
  if (!("caches" in window)) return;
  const keys = await caches.keys();
  await Promise.all(keys.map((key) => caches.delete(key)));
};

const clearIndexedDB = async () => {
  if (!window.indexedDB || typeof indexedDB.databases !== "function") return;
  const databases = await indexedDB.databases();
  await Promise.all(
    databases
      .filter((db) => db.name)
      .map(
        (db) =>
          new Promise((resolve) => {
            const request = indexedDB.deleteDatabase(db.name);
            request.onsuccess = request.onerror = request.onblocked = resolve;
          })
      )
  );
};

// Cookies visíveis ao JS: expira cada um no host atual e nos domínios pai
// (cookie gravado com domain=.dominio.com.br só some se o domain bater).
const clearCookies = () => {
  const host = window.location.hostname;
  const parts = host.split(".");
  const domains = [""];
  for (let i = 0; i < parts.length - 1; i++) {
    const domain = parts.slice(i).join(".");
    domains.push(`; domain=${domain}`, `; domain=.${domain}`);
  }
  const paths = ["/", window.location.pathname];

  document.cookie.split(";").forEach((cookie) => {
    const name = cookie.split("=")[0].trim();
    if (!name) return;
    domains.forEach((domain) => {
      paths.forEach((path) => {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}${domain}`;
      });
    });
  });
};

const clearStorage = () => {
  try {
    localStorage.clear();
  } catch (err) {}
  try {
    sessionStorage.clear();
  } catch (err) {}
};

// fetch direto, sem o axios: os interceptors do `api` tratam 401 redirecionando
// para o /login, o que interromperia a limpeza no meio.
const endBackendSession = async (authorization) => {
  const baseURL = process.env.REACT_APP_BACKEND_URL;
  if (!baseURL) return;
  const headers = authorization ? { Authorization: authorization } : {};
  // logout: marca o usuário offline e apaga o jrt; clear-session apaga o jrt
  // mesmo com o token já vencido.
  await withTimeout(
    fetch(`${baseURL}/auth/logout`, { method: "DELETE", credentials: "include", headers }),
    4000
  );
  await withTimeout(
    fetch(`${baseURL}/auth/clear-session`, { method: "POST", credentials: "include" }),
    4000
  );
};

// O cache HTTP do navegador não se apaga por JS. O que dá para fazer é rebaixar
// as entradas que importam: o index.html (que aponta para os bundles) e o
// manifest. Os bundles têm hash no nome e não precisam disso.
const refreshHttpCache = async () => {
  const urls = ["/", "/index.html", "/login", "/manifest.json", "/asset-manifest.json"];
  await withTimeout(
    Promise.allSettled(urls.map((url) => fetch(url, { cache: "reload", credentials: "same-origin" }))),
    5000
  );
};

const notifyOtherTabs = () => {
  if (typeof BroadcastChannel !== "function") return;
  try {
    const channel = new BroadcastChannel(CLEAR_APP_DATA_CHANNEL);
    channel.postMessage("reload");
    channel.close();
  } catch (err) {}
};

export const clearAppData = async () => {
  // Lido antes de qualquer limpeza: é o que autentica o logout no backend.
  const authorization =
    api.defaults.headers.Authorization || api.defaults.headers.common?.Authorization;

  const steps = [unregisterServiceWorkers, clearCacheStorage, clearIndexedDB];
  for (const step of steps) {
    await withTimeout(step(), 4000);
  }
  clearCookies();
  clearStorage();

  await endBackendSession(authorization);
  await refreshHttpCache();
  notifyOtherTabs();

  // Alguma requisição em andamento pode ter regravado algo durante a limpeza.
  clearStorage();
  window.location.replace("/login");
};

export default clearAppData;
